import { getWorkspaceRoot } from '@/infrastructure/storage/workspace-fs/root'
import { musicgenService } from '@/features/editor/services/musicgen-service'
import { azureTtsService } from '@/features/editor/services/azure-tts-service'
import { kokoroTtsService } from '@/features/editor/services/kokoro-tts-service'
import { mossTtsService } from '@/features/editor/services/moss-tts-service'
import { supertonicTtsService } from '@/features/editor/services/supertonic-tts-service'

export interface AssistantRuntimeCapabilities {
  hasWorker: boolean
  hasWebCodecs: boolean
  hasAudioEncoder: boolean
  hasVideoEncoder: boolean
  hasAudioDecoder: boolean
  hasOffscreenCanvas: boolean
  hasFileSystemAccess: boolean
  hasSharedArrayBuffer: boolean
  hasWorkspaceRoot: boolean
  canImportLocalMedia: boolean
  canPersistProjectData: boolean
  supportsLocalTts: boolean
  supportedTtsEngines: string[]
  supportsLocalMusicGen: boolean
  supportsLocalTranscription: boolean
  supportsLocalExport: boolean
  supportsProgressEvents: boolean
  supportsCancellation: boolean
}

export function getRuntimeCapabilities(): AssistantRuntimeCapabilities {
  const hasWorker = typeof Worker !== 'undefined'
  const hasAudioEncoder = typeof AudioEncoder !== 'undefined'
  const hasVideoEncoder = typeof VideoEncoder !== 'undefined'
  const hasAudioDecoder = typeof AudioDecoder !== 'undefined'
  const hasOffscreenCanvas = typeof OffscreenCanvas !== 'undefined'
  const hasFileSystemAccess = typeof window !== 'undefined' && 'showOpenFilePicker' in window
  const hasSharedArrayBuffer = typeof SharedArrayBuffer !== 'undefined'
  const hasWorkspaceRoot = !!getWorkspaceRoot()
  const supportedTtsEngines = [
    mossTtsService.isSupported() ? 'moss' : null,
    kokoroTtsService.isSupported() ? 'kokoro' : null,
    azureTtsService.isSupported() ? 'azure' : null,
    supertonicTtsService.isSupported() ? 'supertonic' : null,
  ].filter((engine): engine is string => !!engine)

  return {
    hasWorker,
    hasWebCodecs: hasVideoEncoder || hasAudioEncoder || hasAudioDecoder,
    hasAudioEncoder,
    hasVideoEncoder,
    hasAudioDecoder,
    hasOffscreenCanvas,
    hasFileSystemAccess,
    hasSharedArrayBuffer,
    hasWorkspaceRoot,
    canImportLocalMedia: hasFileSystemAccess,
    canPersistProjectData: hasWorkspaceRoot,
    supportsLocalTts: supportedTtsEngines.length > 0,
    supportedTtsEngines,
    supportsLocalMusicGen: musicgenService.isSupported(),
    supportsLocalTranscription: hasWorker,
    supportsLocalExport: hasWorker && hasOffscreenCanvas && (hasVideoEncoder || hasAudioEncoder),
    supportsProgressEvents: true,
    supportsCancellation: true,
  }
}
